import React, { useEffect, useMemo, useState } from "react";
import {
  CButton,
  CCard,
  CCardBody,
  CFormCheck,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CFormTextarea,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CSpinner,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import {
  cilCalendar,
  cilCheckCircle,
  cilCloudUpload,
  cilClock,
  cilDescription,
  cilMediaPause,
  cilPeople,
  cilPlus,
  cilSend,
  cilSpeech,
  cilTrash,
  cilWarning,
  cilX,
} from "@coreui/icons";
import { apiClient } from "services/Api";

const POLL_MS = 3000;
const MAX_ITERATIONS = 2;
const BLANK_BENEFICIARIO = {
  nome: "",
  doravante: "",
  e_lider: false,
  nif: "",
  website: "",
};

const STATUS_LABEL = {
  PENDING: "Pendente",
  IN_PROGRESS: "A decorrer",
  FAILED: "Erro",
  COMPLETED: "Concluído",
};

const STATUS_META = {
  PENDING: {
    className: "is-pending",
    icon: cilMediaPause,
    stepFallback: "Em fila para processamento",
  },
  IN_PROGRESS: {
    className: "is-progress",
    stepFallback: "A tratar o pedido",
  },
  FAILED: {
    className: "is-failed",
    icon: cilWarning,
    stepFallback: "O pedido terminou com erro",
  },
  COMPLETED: {
    className: "is-completed",
    icon: cilCheckCircle,
    stepFallback: "Resposta pronta para rever",
  },
};

const ATTACHMENT_STATUS_LABEL = {
  PENDING: "Em fila",
  PROCESSING: "A processar",
  READY: "Pronto",
  FAILED: "Falhou",
};

const ATTACHMENT_STATUS_CLASS = {
  PENDING: "is-pending",
  PROCESSING: "is-processing",
  READY: "is-ready",
  FAILED: "is-failed",
};

const STEP_LABEL = {
  application_cleaner: "A ler candidatura",
  requirements_builder: "A levantar requisitos",
  context_agent: "A preparar contexto",
  narrative_builder: "A organizar resposta",
  writer_agent: "A redigir texto",
  qa_agent: "A rever texto",
  commit_text: "Texto final preparado",
};

const TIMELINE_LABEL = {
  initial_instruction: "Input inicial",
  revision_instruction: "Pedido de revisão",
  committed_text: "Resposta do assistente",
};

const prettyJson = (value) => JSON.stringify(value ?? {}, null, 2);

const formatDateTime = (value) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString("pt-PT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (error) {
    return value;
  }
};

const buildBeneficiariosDraft = (state) => {
  const source = (Array.isArray(state?.beneficiario) &&
  state.beneficiario.length
    ? state.beneficiario
    : [BLANK_BENEFICIARIO]) || [BLANK_BENEFICIARIO];

  return source.map((item) => ({
    ...BLANK_BENEFICIARIO,
    ...item,
    e_lider: Boolean(item?.e_lider),
  }));
};

const isRowEmpty = (row) =>
  !row?.nome && !row?.doravante && !row?.nif && !row?.website && !row?.e_lider;

const sanitizeBeneficiarios = (value) =>
  (Array.isArray(value) ? value : []).filter((row) => !isRowEmpty(row));

const readPersistedSelection = (key) => {
  if (!key || typeof window === "undefined") return {};
  try {
    return JSON.parse(window.localStorage.getItem(key) || "{}");
  } catch (error) {
    return {};
  }
};

const writePersistedSelection = (key, value) => {
  if (!key || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // ignore
  }
};

const lookupOptionLabel = (options, value) => {
  if (!Array.isArray(options)) {
    return String(value || "").trim();
  }

  const match = options.find(
    (option) => String(option?.value) === String(value),
  );
  return String(match?.label || value || "").trim();
};

const lookupNestedOptionLabel = (optionsByParent, parentValue, value) => {
  if (!optionsByParent || typeof optionsByParent !== "object") {
    return String(value || "").trim();
  }

  return lookupOptionLabel(optionsByParent[String(parentValue)], value);
};

const getLocationLabel = (applicationData, locationId) => {
  const locations = Array.isArray(applicationData?.projectLocation)
    ? applicationData.projectLocation
    : [];
  const location = locations.find(
    (item) => String(item?.id) === String(locationId),
  );

  return (
    location?.description ||
    location?.address ||
    `Estabelecimento #${locationId}`
  );
};

const translateRowSnapshot = (
  applicationData,
  item,
  collectionPath,
  jsonData,
) => {
  const snapshot = { ...item };
  const locationId = item?.location ?? item?.institute;

  if (collectionPath === "investmentsResearchAndDevelopment") {
    snapshot.type_code = item?.type;
    snapshot.type = lookupOptionLabel(jsonData?.InvestmentRDType, item?.type);
    snapshot.researchType_code = item?.researchType;
    snapshot.researchType = lookupNestedOptionLabel(
      jsonData?.InvestmentRDResearchType,
      item?.type,
      item?.researchType,
    );
    snapshot.institute_id = locationId;
    snapshot.institute = locationId
      ? getLocationLabel(applicationData, locationId)
      : "";
    return snapshot;
  }

  if (collectionPath === "investmentsProductiveInnovation") {
    snapshot.type_code = item?.type;
    snapshot.type = lookupOptionLabel(
      jsonData?.InvestmentProductiveInnovationType,
      item?.type,
    );
    snapshot.institute_id = locationId;
    snapshot.institute = locationId
      ? getLocationLabel(applicationData, locationId)
      : "";
    return snapshot;
  }

  if (collectionPath === "investmentsOtherExpenses") {
    snapshot.type_code = item?.type;
    snapshot.type = lookupOptionLabel(
      jsonData?.InvestmentsOtherExpensesType,
      item?.type,
    );
    snapshot.institute_id = locationId;
    snapshot.institute = locationId
      ? getLocationLabel(applicationData, locationId)
      : "";
    return snapshot;
  }

  return snapshot;
};

const buildRowSummary = (applicationData, item, collectionPath, jsonData) => {
  const translatedItem = translateRowSnapshot(
    applicationData,
    item,
    collectionPath,
    jsonData,
  );

  const parts = [
    String(translatedItem?.designation || "").trim(),
    String(translatedItem?.type || "").trim(),
    String(translatedItem?.researchType || "").trim(),
    String(translatedItem?.institute || "").trim(),
  ].filter(Boolean);

  return parts.join(" - ");
};

const buildLiveSelectionOptions = (applicationData, fieldMeta, jsonData) => {
  if (!fieldMeta || fieldMeta.scope === "application") {
    return [];
  }

  const collectionPath = fieldMeta.collection_path;
  const rowIdentifier =
    fieldMeta.row_identifier ||
    (fieldMeta.scope === "per_location" ? "locationId" : "id");
  const fieldKey = fieldMeta.field_key || fieldMeta.form_field_key;
  const suffix = String(fieldKey || "").split("[].");
  const resolvedFieldKey = suffix.length === 2 ? suffix[1] : fieldKey;
  const items = Array.isArray(applicationData?.[collectionPath])
    ? applicationData[collectionPath]
    : [];

  return items
    .filter((item) => item && typeof item === "object")
    .map((item) => {
      const selectedId = item?.[rowIdentifier];
      if (selectedId == null || selectedId === "") {
        return null;
      }

      if (fieldMeta.scope === "per_location") {
        const label = getLocationLabel(applicationData, selectedId);
        return {
          id: String(selectedId),
          label,
          summary: label,
          scope: fieldMeta.scope,
          collection_path: collectionPath,
          row_identifier: rowIdentifier,
          field_key: resolvedFieldKey,
          snapshot: { ...item },
          current_value: item?.[resolvedFieldKey] || "",
        };
      }

      const summary = buildRowSummary(
        applicationData,
        item,
        collectionPath,
        jsonData,
      );

      return {
        id: String(selectedId),
        label: summary || `Linha #${selectedId}`,
        summary,
        scope: fieldMeta.scope,
        collection_path: collectionPath,
        row_identifier: rowIdentifier,
        field_key: resolvedFieldKey,
        snapshot: translateRowSnapshot(
          applicationData,
          item,
          collectionPath,
          jsonData,
        ),
        current_value: item?.[resolvedFieldKey] || "",
      };
    })
    .filter(Boolean);
};

const StreamPilotDrawer = ({
  applicationId,
  notice,
  currentSectionId,
  visible,
  onHide,
  formData,
  jsonData,
  handleChange,
  setToast,
}) => {
  const storageKey = useMemo(
    () => `stream-pilot-workspace:${applicationId}`,
    [applicationId],
  );
  const [workspace, setWorkspace] = useState(null);
  const [loadingWorkspace, setLoadingWorkspace] = useState(false);
  const [selectedFieldId, setSelectedFieldId] = useState("");
  const [selectedItemId, setSelectedItemId] = useState("");
  const [selectedJobId, setSelectedJobId] = useState("");
  const [chatInput, setChatInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [memoryJson, setMemoryJson] = useState("{}");
  const [memoryError, setMemoryError] = useState("");
  const [beneficiarios, setBeneficiarios] = useState([BLANK_BENEFICIARIO]);
  const [showAdvancedMemory, setShowAdvancedMemory] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [activePage, setActivePage] = useState("chat");
  const [isDraftConversation, setIsDraftConversation] = useState(false);

  const supportedFields = workspace?.supported_fields || [];
  const backendSelectedField =
    workspace?.selected_field ||
    supportedFields.find((field) => field.field_id === selectedFieldId) ||
    null;
  const liveSelectionOptions = useMemo(
    () => buildLiveSelectionOptions(formData, backendSelectedField, jsonData),
    [formData, backendSelectedField, jsonData],
  );
  const resolvedSelectedItemId =
    selectedItemId || backendSelectedField?.selected_item_id || "";
  const selectedField = useMemo(() => {
    if (!backendSelectedField) return null;

    if (backendSelectedField.scope === "application") {
      return {
        ...backendSelectedField,
        selection_options: [],
        selected_option: null,
        selected_item_id: "",
        current_value:
          formData?.[backendSelectedField.form_field_key] ??
          backendSelectedField.current_value ??
          "",
      };
    }

    const selectedOption =
      liveSelectionOptions.find(
        (option) => String(option.id) === String(resolvedSelectedItemId),
      ) || null;

    return {
      ...backendSelectedField,
      selection_options: liveSelectionOptions,
      selected_option: selectedOption,
      selected_item_id: selectedOption?.id || resolvedSelectedItemId,
      current_value: selectedOption?.current_value || "",
    };
  }, [
    backendSelectedField,
    formData,
    liveSelectionOptions,
    resolvedSelectedItemId,
  ]);
  const selectedFieldOption = selectedField?.selected_option || null;
  const selectedJob = isDraftConversation
    ? null
    : workspace?.selected_job || null;
  const timelineItems = isDraftConversation ? [] : workspace?.timeline || [];
  const hasChatContent = timelineItems.length > 0;
  const isInitialInput = isDraftConversation || !hasChatContent;
  const statusMeta = selectedJob ? STATUS_META[selectedJob.status] : null;
  const currentActionLabel =
    STEP_LABEL[selectedJob?.current_step] ||
    statusMeta?.stepFallback ||
    "Pronto para receber instruções";
  const beneficiaryMemory = useMemo(
    () => sanitizeBeneficiarios(workspace?.memory?.state?.beneficiario),
    [workspace?.memory?.state?.beneficiario],
  );
  const hasConfiguredBeneficiarios = beneficiaryMemory.length > 0;
  const hasLeaderBeneficiario = beneficiaryMemory.some((row) => row?.e_lider);
  const needsBeneficiaryConfiguration =
    !hasConfiguredBeneficiarios || !hasLeaderBeneficiario;
  const selectionReady =
    Boolean(selectedFieldId) &&
    (selectedField?.scope === "application" || Boolean(selectedFieldOption));
  const needsInstanceSelection =
    Boolean(selectedField) &&
    selectedField?.scope !== "application" &&
    !selectedFieldOption;
  const composerDisabled =
    isSubmitting ||
    !selectionReady ||
    needsBeneficiaryConfiguration ||
    (!isInitialInput && !chatInput.trim()) ||
    (!isDraftConversation &&
      ["PENDING", "IN_PROGRESS"].includes(selectedJob?.status));
  const hasActiveAttachmentProcessing = (workspace?.attachments || []).some(
    (attachment) =>
      attachment?.processing_status === "PENDING" ||
      attachment?.processing_status === "PROCESSING",
  );

  const groupedFields = useMemo(() => {
    const groups = new Map();
    for (const field of supportedFields) {
      const key = field.section_id || "outros";
      if (!groups.has(key)) {
        groups.set(key, {
          section_id: key,
          section_title: field.section_title || key,
          fields: [],
        });
      }
      groups.get(key).fields.push(field);
    }
    return Array.from(groups.values());
  }, [supportedFields]);

  const loadWorkspace = async ({
    fieldId = selectedFieldId,
    nextSelectedItemId = selectedItemId,
    jobId = selectedJobId,
    silent = false,
    skipJobAutoSelect = isDraftConversation,
  } = {}) => {
    if (!applicationId || !visible) return;
    if (!silent) setLoadingWorkspace(true);
    try {
      const response = await apiClient.get(
        `/stream-pilot/application/${applicationId}/workspace/`,
        {
          params: {
            field_id: fieldId || undefined,
            selected_item_id: nextSelectedItemId || undefined,
            job_id: jobId || undefined,
          },
        },
      );
      const nextWorkspace = response.data;
      setWorkspace(nextWorkspace);

      if (!silent || !workspace) {
        setMemoryJson(prettyJson(nextWorkspace?.memory?.state || {}));
        setBeneficiarios(
          buildBeneficiariosDraft(
            nextWorkspace?.memory?.state || {},
            nextWorkspace?.memory_template || {},
          ),
        );
        setMemoryError("");
      }

      if (!fieldId && nextWorkspace?.supported_fields?.length) {
        const persisted = readPersistedSelection(storageKey);
        const preferredField =
          nextWorkspace.supported_fields.find(
            (field) => field.field_id === persisted.fieldId,
          ) ||
          nextWorkspace.supported_fields.find(
            (field) => field.section_id === currentSectionId,
          ) ||
          nextWorkspace.supported_fields[0];
        if (preferredField?.field_id) {
          setSelectedFieldId(preferredField.field_id);
          return;
        }
      }

      const selectedItemFromJob =
        nextWorkspace?.selected_job?.field_selection?.selected_item_id || "";
      const selectedItemFromField =
        nextWorkspace?.selected_field?.selected_item_id || "";
      if (!nextSelectedItemId) {
        if (selectedItemFromJob) {
          setSelectedItemId(String(selectedItemFromJob));
        } else if (selectedItemFromField) {
          setSelectedItemId(String(selectedItemFromField));
        }
      }

      if (!skipJobAutoSelect && !jobId && nextWorkspace?.selected_job?.id) {
        setSelectedJobId(String(nextWorkspace.selected_job.id));
      }
    } catch (error) {
      setToast({
        title: "Assistente de texto",
        message: "Não foi possível carregar o assistente.",
        type: "error",
      });
    } finally {
      if (!silent) setLoadingWorkspace(false);
    }
  };

  useEffect(() => {
    if (!visible) return;
    const persisted = readPersistedSelection(storageKey);
    const draftFieldId = selectedFieldId || persisted.fieldId || "";
    const draftItemId = selectedItemId || persisted.itemId || "";
    const initialFieldId = isDraftConversation
      ? draftFieldId
      : persisted.fieldId || "";
    const initialItemId = isDraftConversation
      ? draftItemId
      : persisted.itemId || "";
    const initialJobId = isDraftConversation ? "" : persisted.jobId || "";

    if (!isDraftConversation) {
      if (persisted.fieldId) setSelectedFieldId(String(persisted.fieldId));
      if (persisted.itemId) setSelectedItemId(String(persisted.itemId));
      if (persisted.jobId) setSelectedJobId(String(persisted.jobId));
    }

    loadWorkspace({
      fieldId: initialFieldId,
      nextSelectedItemId: initialItemId,
      jobId: initialJobId,
      skipJobAutoSelect: isDraftConversation,
    });
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!visible || !selectedFieldId) return;
    writePersistedSelection(storageKey, {
      fieldId: selectedFieldId || "",
      itemId: selectedItemId || "",
      jobId: isDraftConversation ? "" : selectedJobId || "",
    });
  }, [
    visible,
    selectedFieldId,
    selectedItemId,
    selectedJobId,
    isDraftConversation,
    storageKey,
  ]);

  useEffect(() => {
    if (!visible || !selectedFieldId) return;
    loadWorkspace({
      fieldId: selectedFieldId,
      nextSelectedItemId: selectedItemId || "",
      jobId: isDraftConversation ? "" : selectedJobId || "",
      skipJobAutoSelect: isDraftConversation,
    });
  }, [selectedFieldId, selectedItemId, isDraftConversation]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!visible || !selectedFieldId || isDraftConversation || !selectedJobId)
      return;
    const timer = setInterval(() => {
      loadWorkspace({
        fieldId: selectedFieldId,
        nextSelectedItemId: selectedItemId || "",
        jobId: selectedJobId || "",
        silent: true,
        skipJobAutoSelect: false,
      });
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [
    visible,
    selectedFieldId,
    selectedItemId,
    selectedJobId,
    isDraftConversation,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!visible || !hasActiveAttachmentProcessing) return;
    const timer = setInterval(() => {
      loadWorkspace({
        fieldId: selectedFieldId || "",
        nextSelectedItemId: selectedItemId || "",
        jobId: isDraftConversation ? "" : selectedJobId || "",
        silent: true,
        skipJobAutoSelect: isDraftConversation,
      });
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [
    visible,
    hasActiveAttachmentProcessing,
    selectedFieldId,
    selectedItemId,
    selectedJobId,
    isDraftConversation,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!visible || selectedField?.scope === "application") return;
    if (!selectedItemId) return;

    const optionStillExists = (selectedField?.selection_options || []).some(
      (option) => String(option.id) === String(selectedItemId),
    );

    if (!optionStillExists) {
      setSelectedItemId("");
      setSelectedJobId("");
      setIsDraftConversation(false);
    }
  }, [visible, selectedField, selectedItemId]);

  const persistMemoryState = async (nextState) => {
    const response = await apiClient.patch(
      `/stream-pilot/application/${applicationId}/state/`,
      { state: nextState },
    );
    const persistedState = response.data?.state || {};
    setMemoryJson(prettyJson(persistedState));
    setBeneficiarios(
      buildBeneficiariosDraft(persistedState, workspace?.memory_template || {}),
    );
    setMemoryError("");
    await loadWorkspace({
      fieldId: selectedFieldId,
      nextSelectedItemId: selectedItemId || "",
      jobId: isDraftConversation ? "" : selectedJobId || "",
      skipJobAutoSelect: isDraftConversation,
    });
  };

  const handleSelectJob = async (jobId) => {
    setIsDraftConversation(false);
    setSelectedJobId(jobId);
    await loadWorkspace({
      fieldId: selectedFieldId,
      nextSelectedItemId: selectedItemId || "",
      jobId,
      skipJobAutoSelect: false,
    });
  };

  const handleCreateConversation = () => {
    setActivePage("chat");
    setIsDraftConversation(true);
    setSelectedJobId("");
    setChatInput("");
  };

  const handleApplyCommittedText = (text) => {
    if (!selectedField?.form_field_key) return;
    if (!window.confirm("Aplicar este texto ao campo do formulário?")) return;
    if (selectedField.scope === "application") {
      handleChange(selectedField.form_field_key, text || "");
    } else {
      if (
        !selectedItemId ||
        !selectedField.collection_path ||
        !selectedField.field_key
      ) {
        return;
      }
      const rowIdentifier = selectedField.row_identifier || "id";
      const items = Array.isArray(formData?.[selectedField.collection_path])
        ? formData[selectedField.collection_path]
        : [];
      const nextItems = items.map((item) =>
        String(item?.[rowIdentifier]) === String(selectedItemId)
          ? { ...item, [selectedField.field_key]: text || "" }
          : item,
      );
      handleChange(selectedField.collection_path, nextItems);
    }
    setToast({
      title: "Assistente de texto",
      message: "Texto aplicado ao formulário. Falta guardar a candidatura.",
      type: "success",
    });
  };

  const handleGenerate = async (initialMessage = "") => {
    if (!selectedFieldId) return;
    if (selectedField?.scope !== "application" && !selectedFieldOption) return;
    setIsSubmitting(true);
    try {
      const response = await apiClient.post("/stream-pilot/generate-field/", {
        application_id: applicationId,
        field_id: selectedFieldId,
        application_payload:
          formData && typeof formData === "object" ? formData : undefined,
        field_selection:
          selectedField?.scope === "application"
            ? undefined
            : {
                scope: selectedFieldOption?.scope,
                collection_path: selectedFieldOption?.collection_path,
                row_identifier: selectedFieldOption?.row_identifier,
                selected_item_id: String(selectedFieldOption?.id || ""),
                selected_item_label: selectedFieldOption?.label || "",
                field_key:
                  selectedFieldOption?.field_key ||
                  selectedField?.field_key ||
                  "",
              },
        max_iterations: MAX_ITERATIONS,
        custom_instructions: String(initialMessage || "").trim() || undefined,
      });
      const nextJobId = String(response.data?.id || "");
      setIsDraftConversation(false);
      setSelectedJobId(nextJobId);
      await loadWorkspace({
        fieldId: selectedFieldId,
        nextSelectedItemId: selectedItemId || "",
        jobId: nextJobId,
        skipJobAutoSelect: false,
      });
      setChatInput("");
      setToast({
        title: "Assistente de texto",
        message: "Geração iniciada.",
        type: "success",
      });
    } catch (error) {
      setToast({
        title: "Assistente de texto",
        message: "Não foi possível iniciar a geração.",
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResume = async () => {
    const failedJobId = workspace?.latest_failed_job_id;
    if (!failedJobId) return;
    setIsSubmitting(true);
    try {
      await apiClient.post(`/stream-pilot/jobs/${failedJobId}/resume/`);
      setIsDraftConversation(false);
      setSelectedJobId(String(failedJobId));
      await loadWorkspace({
        fieldId: selectedFieldId,
        nextSelectedItemId: selectedItemId || "",
        jobId: String(failedJobId),
        skipJobAutoSelect: false,
      });
      setToast({
        title: "Assistente de texto",
        message: "Conversa retomada.",
        type: "success",
      });
    } catch (error) {
      setToast({
        title: "Assistente de texto",
        message: "Não foi possível retomar a conversa.",
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRevision = async (message) => {
    const text = String(message || "").trim();
    if (!selectedJob?.id || !text) return;
    setIsSubmitting(true);
    try {
      await apiClient.post(
        `/stream-pilot/jobs/${selectedJob.id}/revision-chat/`,
        {
          message: text,
        },
      );
      await loadWorkspace({
        fieldId: selectedFieldId,
        nextSelectedItemId: selectedItemId || "",
        jobId: String(selectedJob.id),
        skipJobAutoSelect: false,
      });
      setChatInput("");
      setToast({
        title: "Assistente de texto",
        message: "Pedido de revisão enviado.",
        type: "success",
      });
    } catch (error) {
      setToast({
        title: "Assistente de texto",
        message: "Não foi possível enviar o pedido de revisão.",
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitChat = () => {
    if (composerDisabled) return;
    if (isInitialInput) {
      handleGenerate(chatInput);
      return;
    }
    handleRevision(chatInput);
  };

  const handleSaveBeneficiarios = async () => {
    const sanitized = sanitizeBeneficiarios(beneficiarios);
    for (const [index, row] of sanitized.entries()) {
      if (!row.nome || !row.doravante || !row.nif) {
        setMemoryError(
          `Entidade #${index + 1}: nome, doravante e NIF são obrigatórios.`,
        );
        return;
      }
    }

    const baseState = (() => {
      try {
        return JSON.parse(memoryJson || "{}");
      } catch (error) {
        return workspace?.memory?.state || {};
      }
    })();

    setIsSubmitting(true);
    try {
      await persistMemoryState({
        ...baseState,
        beneficiario: sanitized,
      });
      setToast({
        title: "Assistente de texto",
        message: "Contexto das entidades atualizado.",
        type: "success",
      });
    } catch (error) {
      setMemoryError("Não foi possível guardar o contexto das entidades.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveAdvancedMemory = async () => {
    let parsed;
    try {
      parsed = JSON.parse(memoryJson);
    } catch (error) {
      setMemoryError(`JSON inválido: ${error.message}`);
      return;
    }

    setIsSubmitting(true);
    try {
      await persistMemoryState(parsed);
      setToast({
        title: "Assistente de texto",
        message: "Configuração avançada atualizada.",
        type: "success",
      });
    } catch (error) {
      setMemoryError("Não foi possível guardar a configuração avançada.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUploadAttachment = async () => {
    if (!selectedFile) return;
    setIsSubmitting(true);
    try {
      const formDataPayload = new FormData();
      formDataPayload.append("file", selectedFile);
      await apiClient.post(
        `/stream-pilot/application/${applicationId}/memory/files/`,
        formDataPayload,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setSelectedFile(null);
      await loadWorkspace({
        fieldId: selectedFieldId,
        nextSelectedItemId: selectedItemId || "",
        jobId: isDraftConversation ? "" : selectedJobId || "",
        skipJobAutoSelect: isDraftConversation,
      });
      setToast({
        title: "Assistente de texto",
        message: "Anexo enviado para processamento.",
        type: "success",
      });
    } catch (error) {
      setToast({
        title: "Assistente de texto",
        message: "Não foi possível adicionar o anexo.",
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm("Remover este anexo do contexto global?")) return;
    setIsSubmitting(true);
    try {
      await apiClient.delete(
        `/stream-pilot/application/${applicationId}/memory/files/${attachmentId}/`,
      );
      await loadWorkspace({
        fieldId: selectedFieldId,
        nextSelectedItemId: selectedItemId || "",
        jobId: isDraftConversation ? "" : selectedJobId || "",
        skipJobAutoSelect: isDraftConversation,
      });
      setToast({
        title: "Assistente de texto",
        message: "Anexo removido.",
        type: "success",
      });
    } catch (error) {
      setToast({
        title: "Assistente de texto",
        message: "Não foi possível remover o anexo.",
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStatusIcon = () => {
    if (!selectedJob || !statusMeta) return null;
    if (selectedJob.status === "IN_PROGRESS") {
      return <CSpinner size="sm" />;
    }
    return <CIcon icon={statusMeta.icon} />;
  };

  return (
    <div
      className={`stream-pilot-shell ${visible ? "is-visible" : ""}`}
      aria-hidden={!visible}
    >
      <CCard className="stream-pilot-panel">
        <CCardBody className="stream-pilot-panel__body">
          {notice !== "06/C05-i14.01/2026" ? (
            <div className="text-muted">Disponível apenas neste aviso.</div>
          ) : loadingWorkspace && !workspace ? (
            <div className="py-5 text-center">
              <CSpinner />
            </div>
          ) : (
            <>
              <div className="stream-pilot-header">
                <div>
                  <div className="stream-pilot-title">Stream/ Pilot</div>
                  <div className="stream-pilot-subtitle">
                    Assistente contextual para a candidatura.
                  </div>
                </div>
                <CButton
                  color="secondary"
                  variant="ghost"
                  className="stream-pilot-close"
                  onClick={onHide}
                >
                  <CIcon icon={cilX} />
                </CButton>
              </div>

              <div className="stream-pilot-tabs">
                <CButton
                  color="secondary"
                  variant="ghost"
                  className={`stream-pilot-tab is-chat ${
                    activePage === "chat" ? "is-active" : ""
                  }`}
                  onClick={() => setActivePage("chat")}
                >
                  <CIcon icon={cilSpeech} className="me-2" />
                  Chat
                </CButton>
                <CButton
                  color="secondary"
                  variant="ghost"
                  className={`stream-pilot-tab is-beneficiarios ${
                    activePage === "beneficiarios" ? "is-active" : ""
                  }`}
                  onClick={() => setActivePage("beneficiarios")}
                >
                  <CIcon icon={cilPeople} className="me-2" />
                  Beneficiários
                </CButton>
                <CButton
                  color="secondary"
                  variant="ghost"
                  className={`stream-pilot-tab is-attachments ${
                    activePage === "attachments" ? "is-active" : ""
                  }`}
                  onClick={() => setActivePage("attachments")}
                >
                  <CIcon icon={cilDescription} className="me-2" />
                  Anexos
                </CButton>
              </div>

              <div className="stream-pilot-content">
                {activePage === "chat" && (
                  <div className="stream-pilot-chat-layout">
                    <CCard className="stream-pilot-surface stream-pilot-selector-card">
                      <CCardBody className="p-3">
                        <div className="row g-2">
                          <div className="col-12">
                            <CFormLabel>Campo</CFormLabel>
                            <CFormSelect
                              value={selectedFieldId}
                              onChange={(e) => {
                                setIsDraftConversation(false);
                                setSelectedFieldId(e.target.value);
                                setSelectedItemId("");
                                setSelectedJobId("");
                              }}
                            >
                              {!selectedFieldId && (
                                <option value="">Selecionar campo...</option>
                              )}
                              {groupedFields.map((group) => (
                                <optgroup
                                  key={group.section_id}
                                  label={group.section_title}
                                >
                                  {group.fields.map((field) => (
                                    <option
                                      key={field.field_id}
                                      value={field.field_id}
                                    >
                                      {field.label}
                                    </option>
                                  ))}
                                </optgroup>
                              ))}
                            </CFormSelect>
                          </div>

                          {selectedField &&
                            selectedField.scope !== "application" && (
                              <div className="col-12">
                                <CFormLabel>Instância</CFormLabel>
                                <CFormSelect
                                  value={selectedItemId}
                                  onChange={(e) => {
                                    setIsDraftConversation(false);
                                    setSelectedItemId(e.target.value);
                                    setSelectedJobId("");
                                  }}
                                >
                                  <option value="">Selecionar...</option>
                                  {(selectedField.selection_options || []).map(
                                    (option) => (
                                      <option key={option.id} value={option.id}>
                                        {option.label}
                                      </option>
                                    ),
                                  )}
                                </CFormSelect>
                                {selectedFieldOption?.summary && (
                                  <div className="stream-pilot-inline-help">
                                    {selectedFieldOption.summary}
                                  </div>
                                )}
                              </div>
                            )}
                        </div>
                      </CCardBody>
                    </CCard>

                    <CCard className="stream-pilot-surface stream-pilot-conversations-card">
                      <CCardBody className="p-3">
                        <div className="stream-pilot-conversations-row">
                          <div className="stream-pilot-conversations-list">
                            {isDraftConversation && (
                              <button
                                type="button"
                                className="stream-pilot-conversation-chip is-active"
                              >
                                <span className="stream-pilot-conversation-chip__title">
                                  Nova conversa
                                </span>
                                <span className="stream-pilot-conversation-chip__meta">
                                  Input inicial
                                </span>
                              </button>
                            )}

                            {(workspace?.jobs || []).map((job) => (
                              <button
                                key={job.id}
                                type="button"
                                className={`stream-pilot-conversation-chip ${
                                  !isDraftConversation &&
                                  String(selectedJobId) === String(job.id)
                                    ? "is-active"
                                    : ""
                                }`}
                                onClick={() => handleSelectJob(String(job.id))}
                              >
                                <span className="stream-pilot-conversation-chip__title">
                                  Conversa #{job.id}
                                </span>
                                <span className="stream-pilot-conversation-chip__meta">
                                  {STATUS_LABEL[job.status] || job.status}
                                </span>
                              </button>
                            ))}

                            {!isDraftConversation &&
                              !(workspace?.jobs || []).length && (
                                <div className="stream-pilot-empty-inline">
                                  Sem conversas ainda.
                                </div>
                              )}
                          </div>

                          <CButton
                            color="primary"
                            className="stream-pilot-new-chat"
                            disabled={!selectionReady || isSubmitting}
                            onClick={handleCreateConversation}
                          >
                            <CIcon icon={cilPlus} className="me-2" />
                            Novo chat
                          </CButton>
                        </div>
                      </CCardBody>
                    </CCard>

                    <CCard className="stream-pilot-surface stream-pilot-chat-card">
                      <CCardBody className="stream-pilot-chat-card__body">
                        <div className="stream-pilot-chat-header">
                          <div className="stream-pilot-chat-header__top">
                            <div>
                              <div className="stream-pilot-chat-title">
                                {isDraftConversation
                                  ? "Nova conversa"
                                  : selectedJob?.id
                                    ? `Conversa #${selectedJob.id}`
                                    : "Sem conversa"}
                              </div>
                              <div className="stream-pilot-chat-context">
                                {selectedFieldOption?.label ||
                                  selectedField?.label ||
                                  "Selecione um campo"}
                              </div>
                            </div>
                            <div className="stream-pilot-chat-meta">
                              <span className="stream-pilot-chat-meta__item">
                                <CIcon icon={cilCalendar} />
                                {selectedJob?.created_at
                                  ? formatDateTime(selectedJob.created_at)
                                  : "Por criar"}
                              </span>
                              <span className="stream-pilot-chat-meta__item">
                                <CIcon icon={cilClock} />
                                {selectedJob?.updated_at
                                  ? formatDateTime(selectedJob.updated_at)
                                  : "Sem updates"}
                              </span>
                            </div>
                          </div>

                          <div
                            className={`stream-pilot-status-row ${
                              isDraftConversation
                                ? "is-draft"
                                : statusMeta?.className || ""
                            }`}
                          >
                            <span className="stream-pilot-status-pill">
                              {isDraftConversation ? (
                                <CIcon icon={cilSpeech} />
                              ) : (
                                renderStatusIcon()
                              )}
                              {isDraftConversation
                                ? "Input inicial"
                                : STATUS_LABEL[selectedJob?.status] ||
                                  "Sem conversa"}
                            </span>
                            <span className="stream-pilot-status-detail">
                              {isDraftConversation
                                ? "Pronto para começar uma nova conversa."
                                : currentActionLabel}
                            </span>
                          </div>
                        </div>

                        <div
                          className={`stream-pilot-messages ${
                            timelineItems.length === 0 ? "is-empty" : ""
                          }`}
                        >
                          {timelineItems.length === 0 ? (
                            <div className="stream-pilot-empty-state">
                              <div className="stream-pilot-empty-state__icon">
                                <CIcon icon={cilSpeech} size="xl" />
                              </div>
                              <div className="stream-pilot-empty-state__title">
                                {needsBeneficiaryConfiguration
                                  ? "Configure os beneficiários antes de gerar."
                                  : needsInstanceSelection
                                    ? "Selecione o beneficiário antes de gerar."
                                    : selectionReady
                                      ? "Escreva o primeiro pedido para iniciar o assistente."
                                      : "Selecione um campo para começar."}
                              </div>
                              <div className="stream-pilot-empty-state__text">
                                {needsBeneficiaryConfiguration
                                  ? hasConfiguredBeneficiarios
                                    ? "Defina a entidade líder na memória global para desbloquear o assistente."
                                    : "Adicione pelo menos um beneficiário na memória global para desbloquear o assistente."
                                  : needsInstanceSelection
                                    ? "Escolha a entidade certa para gerar o texto no contexto correto."
                                    : "O Stream-Pilot vai usar o contexto da candidatura para gerar e rever o texto."}
                              </div>
                            </div>
                          ) : (
                            <div className="stream-pilot-message-list">
                              {timelineItems.map((item, index) => {
                                const isAssistantMessage =
                                  item.type === "committed_text";
                                return (
                                  <div
                                    key={`${item.type}-${item.job_id}-${index}`}
                                    className={`stream-pilot-message ${
                                      isAssistantMessage
                                        ? "is-assistant"
                                        : "is-user"
                                    }`}
                                  >
                                    <div className="stream-pilot-message__meta">
                                      <span className="stream-pilot-message__type">
                                        {TIMELINE_LABEL[item.type] || item.type}
                                      </span>
                                      <span>
                                        {formatDateTime(item.created_at)}
                                      </span>
                                    </div>
                                    <div className="stream-pilot-message__bubble">
                                      {item.text}
                                    </div>
                                    {item.type === "committed_text" && (
                                      <div className="stream-pilot-message__actions">
                                        <CButton
                                          color="success"
                                          size="sm"
                                          variant="outline"
                                          disabled={
                                            selectedField?.scope !==
                                              "application" &&
                                            !selectedFieldOption
                                          }
                                          onClick={() =>
                                            handleApplyCommittedText(item.text)
                                          }
                                        >
                                          Aplicar ao formulário
                                        </CButton>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        <div className="stream-pilot-composer">
                          {needsBeneficiaryConfiguration && (
                            <div className="stream-pilot-inline-warning is-blocking">
                              <CIcon icon={cilWarning} />
                              <div>
                                <div className="stream-pilot-inline-warning__title">
                                  Configure os beneficiários na memória.
                                </div>
                                <div className="stream-pilot-inline-warning__text">
                                  {hasConfiguredBeneficiarios
                                    ? "Falta definir a entidade líder antes de gerar ou rever texto."
                                    : "Adicione pelo menos um beneficiário e identifique a entidade líder antes de gerar ou rever texto."}
                                </div>
                              </div>
                            </div>
                          )}
                          {needsInstanceSelection && (
                            <div className="stream-pilot-inline-warning is-blocking">
                              <CIcon icon={cilWarning} />
                              <div>
                                <div className="stream-pilot-inline-warning__title">
                                  Selecione a instância antes de avançar.
                                </div>
                                <div className="stream-pilot-inline-warning__text">
                                  Configure o beneficiário correto neste campo
                                  antes de gerar texto.
                                </div>
                              </div>
                            </div>
                          )}
                          {!needsInstanceSelection &&
                            !needsBeneficiaryConfiguration && (
                              <div className="stream-pilot-composer__row">
                                <CFormTextarea
                                  rows={3}
                                  className="stream-pilot-composer__input"
                                  value={chatInput}
                                  placeholder={
                                    !selectionReady
                                      ? "Selecione o campo para começar."
                                      : isInitialInput
                                        ? "Descreva o objetivo do texto."
                                        : "Indique o ajuste pretendido."
                                  }
                                  disabled={
                                    !isDraftConversation &&
                                    ["PENDING", "IN_PROGRESS"].includes(
                                      selectedJob?.status,
                                    )
                                  }
                                  onChange={(e) => setChatInput(e.target.value)}
                                />

                                {selectedJob?.status === "FAILED" ? (
                                  <CButton
                                    color="warning"
                                    className="stream-pilot-submit"
                                    disabled={
                                      isSubmitting ||
                                      !workspace?.latest_failed_job_id
                                    }
                                    onClick={handleResume}
                                  >
                                    {isSubmitting ? (
                                      <CSpinner size="sm" className="me-2" />
                                    ) : (
                                      <CIcon
                                        icon={cilWarning}
                                        className="me-2"
                                      />
                                    )}
                                    Tentar de novo
                                  </CButton>
                                ) : (
                                  <CButton
                                    color="primary"
                                    className={`stream-pilot-submit ${
                                      isInitialInput ? "is-initial" : ""
                                    }`}
                                    disabled={composerDisabled}
                                    onClick={handleSubmitChat}
                                  >
                                    {isSubmitting ? (
                                      <CSpinner size="sm" className="me-2" />
                                    ) : (
                                      <CIcon icon={cilSend} className="me-2" />
                                    )}
                                    {isInitialInput ? "Gerar" : "Enviar"}
                                  </CButton>
                                )}
                              </div>
                            )}
                        </div>
                      </CCardBody>
                    </CCard>
                  </div>
                )}

                {activePage === "attachments" && (
                  <div className="stream-pilot-section-stack">
                    <CCard className="stream-pilot-surface stream-pilot-upload-card">
                      <CCardBody className="p-3">
                        <div className="stream-pilot-section-title">
                          Anexos de contexto
                        </div>
                        <div className="stream-pilot-upload-row">
                          <CFormInput
                            type="file"
                            className="stream-pilot-file-input"
                            onChange={(e) =>
                              setSelectedFile(e.target.files?.[0] || null)
                            }
                          />
                          <CButton
                            color="primary"
                            className="stream-pilot-upload-button"
                            disabled={isSubmitting || !selectedFile}
                            onClick={handleUploadAttachment}
                          >
                            <CIcon icon={cilCloudUpload} className="me-2" />
                            Adicionar
                          </CButton>
                        </div>
                        {selectedFile && (
                          <div className="stream-pilot-inline-help mb-0 mt-2">
                            Ficheiro selecionado: {selectedFile.name}
                          </div>
                        )}
                      </CCardBody>
                    </CCard>

                    <div className="stream-pilot-list">
                      {(workspace?.attachments || []).length ? (
                        (workspace?.attachments || []).map((attachment) => (
                          <CCard
                            key={attachment.id}
                            className="stream-pilot-surface"
                          >
                            <CCardBody className="p-3">
                              <div className="stream-pilot-list-item">
                                <div>
                                  <div className="stream-pilot-list-item__title">
                                    {attachment.filename}
                                  </div>
                                  <div className="stream-pilot-list-item__meta-row">
                                    <div className="stream-pilot-list-item__meta">
                                      {formatDateTime(attachment.created_at)}
                                    </div>
                                    <span
                                      className={`stream-pilot-state-pill ${
                                        ATTACHMENT_STATUS_CLASS[
                                          attachment.processing_status
                                        ] || ""
                                      }`}
                                    >
                                      {ATTACHMENT_STATUS_LABEL[
                                        attachment.processing_status
                                      ] ||
                                        attachment.processing_status ||
                                        "Sem estado"}
                                    </span>
                                  </div>
                                  {attachment.processing_status === "FAILED" &&
                                    attachment.processing_error && (
                                      <div className="stream-pilot-list-item__text is-error">
                                        {attachment.processing_error}
                                      </div>
                                    )}
                                  {attachment.processing_status !== "FAILED" &&
                                    attachment.processing_status !== "READY" && (
                                      <div className="stream-pilot-list-item__text is-muted">
                                        {attachment.processing_status ===
                                        "PROCESSING"
                                          ? "O anexo está a ser analisado e ainda não entra no contexto dos agentes."
                                          : "O anexo foi recebido e está em fila para processamento."}
                                      </div>
                                    )}
                                  {attachment.processing_status === "READY" &&
                                    attachment?.document_summary_index?.title && (
                                    <div className="stream-pilot-list-item__text">
                                      {attachment.document_summary_index.title}
                                    </div>
                                  )}
                                  {attachment.processing_status === "READY" &&
                                    attachment?.document_summary_index
                                      ?.summary_short && (
                                    <div className="stream-pilot-list-item__text is-muted">
                                      {
                                        attachment.document_summary_index
                                          .summary_short
                                      }
                                    </div>
                                  )}
                                </div>
                                <CButton
                                  color="danger"
                                  variant="outline"
                                  size="sm"
                                  disabled={isSubmitting}
                                  onClick={() =>
                                    handleDeleteAttachment(attachment.id)
                                  }
                                >
                                  <CIcon icon={cilTrash} className="me-2" />
                                  Remover
                                </CButton>
                              </div>
                            </CCardBody>
                          </CCard>
                        ))
                      ) : (
                        <div className="stream-pilot-empty-inline">
                          Ainda não existem anexos neste contexto.
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activePage === "beneficiarios" && (
                  <div className="stream-pilot-section-stack">
                    <div className="stream-pilot-list">
                      {beneficiarios.map((row, index) => (
                        <CCard
                          key={`benef-${index}`}
                          className="stream-pilot-surface"
                        >
                          <CCardBody className="p-3">
                            <div className="stream-pilot-list-item">
                              <div className="stream-pilot-list-item__title">
                                Entidade #{index + 1}
                              </div>
                              <CButton
                                color="danger"
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  setBeneficiarios((current) =>
                                    current.length === 1
                                      ? [BLANK_BENEFICIARIO]
                                      : current.filter(
                                          (_, itemIndex) => itemIndex !== index,
                                        ),
                                  )
                                }
                              >
                                Remover
                              </CButton>
                            </div>

                            <div className="row g-3 mt-1">
                              <div className="col-12">
                                <CFormLabel>Nome</CFormLabel>
                                <CFormInput
                                  value={row.nome}
                                  onChange={(e) =>
                                    setBeneficiarios((current) =>
                                      current.map((item, itemIndex) =>
                                        itemIndex === index
                                          ? { ...item, nome: e.target.value }
                                          : item,
                                      ),
                                    )
                                  }
                                />
                              </div>
                              <div className="col-md-6">
                                <CFormLabel>Doravante</CFormLabel>
                                <CFormInput
                                  value={row.doravante}
                                  onChange={(e) =>
                                    setBeneficiarios((current) =>
                                      current.map((item, itemIndex) =>
                                        itemIndex === index
                                          ? {
                                              ...item,
                                              doravante: e.target.value,
                                            }
                                          : item,
                                      ),
                                    )
                                  }
                                />
                              </div>
                              <div className="col-md-6">
                                <CFormLabel>NIF</CFormLabel>
                                <CFormInput
                                  value={row.nif}
                                  onChange={(e) =>
                                    setBeneficiarios((current) =>
                                      current.map((item, itemIndex) =>
                                        itemIndex === index
                                          ? { ...item, nif: e.target.value }
                                          : item,
                                      ),
                                    )
                                  }
                                />
                              </div>
                              <div className="col-12">
                                <CFormLabel>Website</CFormLabel>
                                <CFormInput
                                  value={row.website}
                                  onChange={(e) =>
                                    setBeneficiarios((current) =>
                                      current.map((item, itemIndex) =>
                                        itemIndex === index
                                          ? { ...item, website: e.target.value }
                                          : item,
                                      ),
                                    )
                                  }
                                />
                              </div>
                              <div className="col-12">
                                <CFormCheck
                                  label="É líder"
                                  checked={Boolean(row.e_lider)}
                                  onChange={(e) =>
                                    setBeneficiarios((current) =>
                                      current.map((item, itemIndex) =>
                                        itemIndex === index
                                          ? {
                                              ...item,
                                              e_lider: e.target.checked,
                                            }
                                          : item,
                                      ),
                                    )
                                  }
                                />
                              </div>
                            </div>
                          </CCardBody>
                        </CCard>
                      ))}

                      <CCard className="stream-pilot-surface">
                        <CCardBody className="p-3">
                          <div className="d-flex gap-2 flex-wrap">
                            <CButton
                              color="secondary"
                              variant="outline"
                              className="stream-pilot-action-btn is-add"
                              onClick={() =>
                                setBeneficiarios((current) => [
                                  ...current,
                                  { ...BLANK_BENEFICIARIO },
                                ])
                              }
                            >
                              <CIcon icon={cilPlus} className="me-2" />
                              Adicionar entidade
                            </CButton>
                            <CButton
                              color="success"
                              className="stream-pilot-action-btn is-save"
                              disabled={isSubmitting}
                              onClick={handleSaveBeneficiarios}
                            >
                              <CIcon icon={cilCheckCircle} className="me-2" />
                              Guardar entidades
                            </CButton>
                          </div>
                        </CCardBody>
                      </CCard>
                    </div>

                    <CCard className="stream-pilot-surface">
                      <CCardBody className="p-3">
                        <div className="d-flex gap-2 flex-wrap">
                          <CButton
                            color="secondary"
                            variant="outline"
                            onClick={() => setShowAdvancedMemory(true)}
                          >
                            Configuração avançada
                          </CButton>
                        </div>
                        {memoryError && (
                          <div className="text-danger mt-2">{memoryError}</div>
                        )}
                      </CCardBody>
                    </CCard>
                  </div>
                )}
              </div>
            </>
          )}
        </CCardBody>
      </CCard>

      <CModal
        visible={showAdvancedMemory}
        onClose={() => setShowAdvancedMemory(false)}
        size="lg"
      >
        <CModalHeader>
          <CModalTitle>Configuração avançada</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <div className="stream-pilot-inline-help mb-3 mt-0">
            Use esta área apenas para ajustes avançados da memória global.
          </div>
          <CFormTextarea
            rows={12}
            value={memoryJson}
            onChange={(e) => setMemoryJson(e.target.value)}
          />
          {memoryError && <div className="text-danger mt-2">{memoryError}</div>}
        </CModalBody>
        <CModalFooter>
          <CButton
            color="secondary"
            variant="ghost"
            onClick={() => setShowAdvancedMemory(false)}
          >
            Fechar
          </CButton>
          <CButton
            color="secondary"
            disabled={isSubmitting}
            onClick={handleSaveAdvancedMemory}
          >
            Guardar configuração
          </CButton>
        </CModalFooter>
      </CModal>
    </div>
  );
};

export default StreamPilotDrawer;
